import React from 'react';
import { Link } from 'react-router-dom';
import calendarService from '../../services/calendarService';

/**
 * Events Section Component
 * Shows live events with priority, then upcoming events
 */
const EventsSection = ({ currentEvent, upcomingEvents, loading, error, theme, isMobile, occasionMap }) => {
  // Helper: get Hijri date + occasion for an event's start_date
  const getHijriAnnotation = (event) => {
    if (!occasionMap || !event?.start_date) return null;
    return occasionMap.get(event.start_date) || null;
  };

  // Render Hijri date line under Gregorian date
  const renderHijriLine = (event) => {
    const data = getHijriAnnotation(event);
    if (!data?.hijri) return null;
    const { hijri, occasions } = data;
    const monthName = hijri.monthAbbr || '';
    const hasOccasion = occasions && occasions.length > 0;
    const occasion = hasOccasion ? occasions[0] : null;
    const occasionColor = occasion?.type === 'mourning' ? '#555'
      : occasion?.type === 'joyous' ? (theme?.colors?.primary || '#1A5632')
      : '#64748b';

    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        fontSize: '12px', color: hasOccasion ? occasionColor : '#94a3b8',
        fontWeight: hasOccasion ? 500 : 400,
        marginTop: '1px',
      }}>
        <span>☪</span>
        <span>
          {hijri.day} {monthName}
          {hasOccasion && ` — ${occasion.nameEn}`}
        </span>
      </div>
    );
  };
  // Check if an event is currently live
  const isEventLive = (event) => {
    if (!event) return false;
    
    const now = new Date();
    const [startYear, startMonth, startDay] = event.start_date.split('-');
    const [endYear, endMonth, endDay] = (event.end_date || event.start_date).split('-');
    
    // Parse start time
    let start;
    if (event.start_time) {
      const [startHour, startMin] = event.start_time.split(':');
      start = new Date(startYear, startMonth - 1, startDay, parseInt(startHour), parseInt(startMin));
    } else {
      start = new Date(startYear, startMonth - 1, startDay, 0, 0);
    }
    
    // Parse end time
    let end;
    if (event.end_time) {
      const [endHour, endMin] = event.end_time.split(':');
      end = new Date(endYear, endMonth - 1, endDay, parseInt(endHour), parseInt(endMin));
    } else {
      end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
    }
    
    return now >= start && now <= end;
  };

  // Check if event is today
  const isEventToday = (event) => {
    if (!event) return false;
    
    const today = new Date();
    const [year, month, day] = event.start_date.split('-');
    const eventDate = new Date(year, month - 1, day);
    
    return today.toDateString() === eventDate.toDateString();
  };

  // Format event time
  const formatEventTime = (event) => {
    if (!event.start_time) return '';
    
    const [hours, minutes] = event.start_time.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Emoji icon from centralized category map
  const getEventIcon = (eventType) => {
    const emojis = calendarService.constructor.EVENT_TYPE_EMOJIS;
    return emojis[eventType] || emojis.general;
  };

  // Get relative date string with actual date
  const getRelativeDate = (event) => {
    // Parse date without timezone to avoid issues
    const [year, month, day] = event.start_date.split('-');
    const eventDate = new Date(year, month - 1, day);
    
    // For event start time, use the same approach
    const eventStart = event.start_time 
      ? new Date(year, month - 1, day, 
          parseInt(event.start_time.split(':')[0]), 
          parseInt(event.start_time.split(':')[1]))
      : eventDate;
    
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    
    const daysDiff = Math.floor((eventDate - today) / (1000 * 60 * 60 * 24));
    
    // Format the actual date
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dateStr = `${monthNames[eventDate.getMonth()]} ${eventDate.getDate()}`;
    const dayStr = dayNames[eventDate.getDay()];
    
    if (daysDiff === 0) {
      // Check if event is later today
      if (eventStart > now) {
        const hoursUntil = Math.floor((eventStart - now) / (1000 * 60 * 60));
        if (hoursUntil < 1) {
          const minsUntil = Math.floor((eventStart - now) / (1000 * 60));
          return `In ${minsUntil} min (${dateStr})`;
        } else if (hoursUntil === 1) {
          return `In 1 hour (${dateStr})`;
        } else {
          return `In ${hoursUntil} hours (${dateStr})`;
        }
      }
      return `Today (${dateStr})`;
    }
    
    if (daysDiff === 1) {
      return `Tomorrow (${dateStr}, ${dayStr})`;
    }
    
    if (daysDiff > 1 && daysDiff <= 6) {
      // Within a week - show weekday with date
      const weekday = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
      return `${weekday} (${dateStr})`;
    }
    
    if (daysDiff > 6 && daysDiff <= 30) {
      // Beyond a week but within a month
      return `${dateStr}, ${dayStr}`;
    }
    
    // For dates beyond 30 days or next year
    const currentYear = today.getFullYear();
    if (eventDate.getFullYear() > currentYear) {
      return `${dateStr}, ${eventDate.getFullYear()}`;
    }
    
    return dateStr;
  };

  // Styles
  const styles = {
    section: {
      marginTop: '0',
      marginBottom: '52px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: '600',
      color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a',
      margin: 0
    },
    viewAllLink: {
      color: theme?.colors?.linkColor || theme?.colors?.primary || '#0f172a',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    liveEventHero: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
      border: '2px solid #dc2626',
      position: 'relative',
      overflow: 'hidden'
    },
    liveIndicator: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      backgroundColor: '#dc2626',
      color: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    liveDot: {
      width: '8px',
      height: '8px',
      backgroundColor: 'white',
      borderRadius: '50%',
      animation: 'pulse 1.5s infinite'
    },
    eventCard: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '14px 16px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      border: '1px solid #e2e8f0',
      borderLeftWidth: '3px',
      borderLeftColor: theme?.colors?.primary || '#0f172a',
      transition: 'all 0.2s',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'block',
      position: 'relative'
    },
    eventCardHover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
    },
    todayCard: {
      backgroundColor: `${theme?.colors?.primary}08` || '#0f172a08',
      border: `1px solid ${theme?.colors?.primary}30` || '#0f172a30'
    },
    eventTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme?.colors?.primary || '#0f172a',
      marginBottom: '6px'
    },
    eventDetails: {
      fontSize: '14px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    eventDateTime: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '6px',
      gap: '12px',
      position: 'relative'
    },
    eventDate: {
      fontSize: '13px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      width: isMobile ? '180px' : 'auto',
      flexShrink: 0
    },
    eventTime: {
      fontSize: '13px',
      color: theme?.colors?.primary || '#0f172a',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      whiteSpace: 'nowrap',
      position: isMobile ? 'absolute' : 'static',
      left: isMobile ? '180px' : 'auto',
      top: isMobile ? '50%' : 'auto',
      transform: isMobile ? 'translateY(-50%)' : 'none'
    },
    eventType: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: `${theme?.colors?.primary}15` || '#0f172a15',
      color: theme?.colors?.primary || '#0f172a',
      marginTop: '8px'
    },
    joinButton: {
      backgroundColor: '#dc2626',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '16px',
      width: '100%',
      transition: 'background-color 0.2s'
    },
    upcomingGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '16px',
      marginTop: '20px'
    },
    noEvents: {
      textAlign: 'center',
      padding: '40px',
      backgroundColor: 'white',
      borderRadius: '12px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.colors?.textSecondary || '#64748b'
    },
    error: {
      textAlign: 'center',
      padding: '20px',
      color: theme?.colors?.error || '#dc2626',
      backgroundColor: '#fef2f2',
      borderRadius: '8px'
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.section}>
        <div style={styles.loading}>Loading events...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.section}>
        <div style={styles.error}>Failed to load events</div>
      </div>
    );
  }

  // No events
  if (!currentEvent && (!upcomingEvents || upcomingEvents.length === 0)) {
    return (
      <div style={styles.section}>
        <div style={styles.header}>
          <h2 style={styles.title}>Upcoming Events</h2>
          <Link to="/community/calendar" style={styles.viewAllLink}>
            View Calendar →
          </Link>
        </div>
        <div style={styles.noEvents}>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No upcoming events</p>
          <p style={{ fontSize: '14px' }}>
            {occasionMap && occasionMap.size > 0
              ? 'View the calendar for Islamic occasions and important dates'
              : 'Check back later for updates!'}
          </p>
        </div>
      </div>
    );
  }

  // Check if current event is live
  const isLive = isEventLive(currentEvent);
  const isToday = !isLive && isEventToday(currentEvent);

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          {isLive ? 'Happening Now' : 'Upcoming Events'}
        </h2>
        <Link to="/community/calendar" style={styles.viewAllLink}>
          View Calendar →
        </Link>
      </div>

      {/* Live Event Hero */}
      {isLive && currentEvent && (
        <div style={styles.liveEventHero}>
          <div style={{...styles.liveIndicator, background: 'linear-gradient(135deg, #10b981, #059669)', padding: '6px 12px', borderRadius: '6px'}}>
            <span style={styles.liveDot}></span>
            HAPPENING NOW
          </div>
          <h3 style={styles.eventTitle}>
            <span style={{ marginRight: '8px' }}>{getEventIcon(currentEvent.event_type)}</span>
            {currentEvent.title}
          </h3>
          <div style={styles.eventDetails}>
            <div style={styles.eventDateTime}>
              <div style={styles.eventDate}>
                <span>📅</span>
                <span>Happening Now</span>
              </div>
              <div style={styles.eventTime}>
                <span>⏰</span>
                <span>Started at {formatEventTime(currentEvent)}</span>
              </div>
            </div>
            {renderHijriLine(currentEvent)}
            {currentEvent.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>📍</span>
                <span>{currentEvent.location}</span>
              </div>
            )}
            {currentEvent.metadata?.sponsor_name && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '10px',
                padding: '5px 12px',
                borderRadius: '20px',
                backgroundColor: `${theme?.colors?.primary}10` || '#1a563210',
                border: `1px solid ${theme?.colors?.primary}25` || '#1a563225',
                fontSize: '12px',
                color: theme?.colors?.primary || '#1a5632',
                fontWeight: '500'
              }}>
                <span style={{ fontSize: '14px' }}>🤝</span>
                Sponsored by {currentEvent.metadata.sponsor_name}
              </div>
            )}
            {currentEvent.description && (
              <div style={{
                marginTop: '12px',
                padding: '12px 16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                borderLeft: `3px solid ${theme?.colors?.primary || '#1a5632'}`,
                fontSize: '13px',
                lineHeight: '1.8',
                color: '#334155',
                whiteSpace: 'pre-line'
              }}>
                {currentEvent.description}
              </div>
            )}
          </div>
          {currentEvent.streaming_url && (
            <button 
              style={styles.joinButton}
              onClick={() => window.open(currentEvent.streaming_url, '_blank')}
            >
              Join Live Stream
            </button>
          )}
        </div>
      )}

      {/* Today's Event (if not live) */}
      {!isLive && isToday && currentEvent && (
        <Link 
          to={`/community/calendar`}
          style={{ ...styles.eventCard, ...styles.todayCard }}
        >
          <div style={{ position: 'relative' }}>
            <h3 style={styles.eventTitle}>
              <span style={{ marginRight: '8px' }}>{getEventIcon(currentEvent.event_type)}</span>
              {currentEvent.title}
            </h3>
            <div style={styles.eventDetails}>
              <div style={styles.eventDateTime}>
                <div style={styles.eventDate}>
                  <span>📅</span>
                  <span>{getRelativeDate(currentEvent)}</span>
                </div>
                <div style={styles.eventTime}>
                  <span>⏰</span>
                  <span>{formatEventTime(currentEvent)}</span>
                </div>
              </div>
              {renderHijriLine(currentEvent)}
              {currentEvent.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>📍</span>
                  <span>{currentEvent.location}</span>
                </div>
              )}
              {currentEvent.metadata?.sponsor_name && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  marginTop: '6px', padding: '3px 10px', borderRadius: '20px',
                  backgroundColor: `${theme?.colors?.primary}10` || '#1a563210',
                  border: `1px solid ${theme?.colors?.primary}25` || '#1a563225',
                  fontSize: '11px', color: theme?.colors?.primary || '#1a5632', fontWeight: '500'
                }}>
                  <span style={{ fontSize: '12px' }}>🤝</span>
                  Sponsored by {currentEvent.metadata.sponsor_name}
                </div>
              )}
              {currentEvent.description && (
                <p style={{
                  marginTop: '8px', fontSize: '12px', lineHeight: '1.6',
                  color: '#64748b', whiteSpace: 'pre-line',
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'
                }}>
                  {currentEvent.description}
                </p>
              )}
            </div>
          </div>
        </Link>
      )}

      {/* Upcoming Events Grid */}
      {upcomingEvents && upcomingEvents.length > 0 && (
        <div style={styles.upcomingGrid}>
          {upcomingEvents.slice(0, isLive ? 2 : 3).map((event) => (
            <Link 
              key={event.id}
              to={`/community/calendar`}
              style={styles.eventCard}
            >
              <h3 style={styles.eventTitle}>
                <span style={{ marginRight: '8px' }}>{getEventIcon(event.event_type)}</span>
                {event.title}
              </h3>
              <div style={styles.eventDetails}>
                <div style={styles.eventDateTime}>
                  <div style={styles.eventDate}>
                    <span>📅</span>
                    <span>{getRelativeDate(event)}</span>
                  </div>
                  {event.start_time && (
                    <div style={styles.eventTime}>
                      <span>⏰</span>
                      <span>{formatEventTime(event)}</span>
                    </div>
                  )}
                </div>
                {renderHijriLine(event)}
                {event.location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>📍</span>
                    <span>{event.location}</span>
                  </div>
                )}
                {event.metadata?.sponsor_name && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    marginTop: '6px', padding: '3px 10px', borderRadius: '20px',
                    backgroundColor: `${theme?.colors?.primary}10` || '#1a563210',
                    border: `1px solid ${theme?.colors?.primary}25` || '#1a563225',
                    fontSize: '11px', color: theme?.colors?.primary || '#1a5632', fontWeight: '500'
                  }}>
                    <span style={{ fontSize: '12px' }}>🤝</span>
                    Sponsored by {event.metadata.sponsor_name}
                  </div>
                )}
                {event.description && (
                  <p style={{
                    marginTop: '6px', fontSize: '12px', lineHeight: '1.5',
                    color: '#64748b', whiteSpace: 'pre-line', margin: '6px 0 0',
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                  }}>
                    {event.description}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Add pulse animation */}
      <style>{`
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default EventsSection;